import "./Branches.css";
import MarkerIcon from "../../../assets/icons/location-position.png";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import { Col, Form, Row } from "react-bootstrap";
import Button from "@mui/material/Button";
import { MdAddLocationAlt } from "react-icons/md";
import Tooltip from "@mui/material/Tooltip";
import { ToastContainer } from "react-toastify";
import { useGlobalToast } from "../../../stopOldToastGlobally.js";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { initState, saveBranch } from "../../../redux/slices/branchSlice";
import { commonSaveData, initCommonState } from "../../../redux/slices/commonSlice";
import { updateBranchPath } from "../../../services/config";

const containerStyle = { width: "100%", height: "90vh" };
const libraries = ["places"];

/* ── Coordinate guards ──────────────────────────────────────────
   NEVER pass null/NaN to GoogleMap, Circle, or Marker.
   Delhi is the fallback when real coords aren't available yet.
─────────────────────────────────────────────────────────────── */
const DEFAULT_CENTER = { lat: 28.6139, lng: 77.2090 };

const safeFloat = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const isValidLatLng = (c) =>
  c != null &&
  typeof c.lat === "number" && Number.isFinite(c.lat) &&
  typeof c.lng === "number" && Number.isFinite(c.lng);

const safeCenter = (c) => (isValidLatLng(c) ? c : DEFAULT_CENTER);

const darkModeStyle = [
  { elementType: "geometry",            stylers: [{ color: "#010113" }] },
  { elementType: "labels.icon",         stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill",    stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke",  stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry",         stylers: [{ color: "#757575" }] },
  { featureType: "poi",            elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road",           elementType: "geometry",         stylers: [{ color: "#383838" }] },
  { featureType: "water",          elementType: "geometry",         stylers: [{ color: "#000000" }] },
  { featureType: "water",          elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] },
];

function MapComponent({ isEdit, editingData, actionsDone, handleCloseDialog }) {
  const toast     = useGlobalToast();
  const dispatch  = useDispatch();
  const state     = useSelector((s) => s.branch);
  const commonState = useSelector((s) => s.common);
  const theme     = localStorage.getItem("theme") || "light";

  /* Safe user parse — never throws */
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user_data")) || {}; }
    catch { return {}; }
  })();

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });

  /* ── state ── */
  const [map,              setMap]              = useState(null);
  /* CENTER IS ALWAYS VALID — initialized to Delhi, never null */
  const [center,           setCenter]           = useState(DEFAULT_CENTER);
  const [radius,           setRadius]           = useState("");
  const [zoom,             setZoom]             = useState(15);
  const [circleInstance,   setCircleInstance]   = useState(null);
  const [inputValue,       setInputValue]       = useState("");
  const [dropdownVisible,  setDropdownVisible]  = useState(false);
  const [options,          setOptions]          = useState([]);
  const [validated,        setValidated]        = useState(false);
  const [isLoading,        setIsLoading]        = useState(false);
  const [locationError,    setLocationError]    = useState(null);
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const [isGettingLocation,setIsGettingLocation]= useState(false);

  const [addBranch, setAddBranch] = useState({
    branchId:      null,
    userId:        user.user_id,
    branchName:    "",
    branchAddress: "",
    radius:        "",
    latitude:      null,
    longitude:     null,
    accuracy:      null,
  });

  /* mapOptions built after isLoaded so ControlPosition is available */
  const mapOptions = {
    streetViewControl:        false,
    mapTypeControl:           false,
    minZoom:                  5,
    zoomControl:              true,
    zoomControlOptions: {
      position: isLoaded
        ? window.google?.maps?.ControlPosition?.LEFT_BOTTOM ?? 6
        : 6,
    },
    fullscreenControl:        true,
    fullscreenControlOptions: {
      position: isLoaded
        ? window.google?.maps?.ControlPosition?.LEFT_TOP ?? 1
        : 1,
    },
    styles:          theme === "dark" ? darkModeStyle : [],
    disableDefaultUI: false,
  };

  /* ── address from coords ── */
  const getDetailedAddress = useCallback(async (lat, lng) => {
    try {
      const res = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
      );
      if (res.data.results?.length > 0) {
        const exact = res.data.results.find((r) =>
          r.geometry.location_type === "ROOFTOP" ||
          r.types.includes("street_address") ||
          r.types.includes("premise")
        );
        return exact
          ? exact.formatted_address
          : res.data.results[0].formatted_address;
      }
    } catch (err) {
      console.error("Geocode error:", err);
    }
    return `${lat}, ${lng}`;
  }, []);

  /* ── zoom listener ── */
  useEffect(() => {
    if (!map) return;
    setZoom(map.getZoom());
    const listener = map.addListener("zoom_changed", () => setZoom(map.getZoom()));
    return () => window.google?.maps?.event?.removeListener(listener);
  }, [map]);

  /* ── initialise edit data ── */
  useEffect(() => {
    if (!isEdit || !editingData) return;

    const lat = safeFloat(editingData.latitude)  ?? DEFAULT_CENTER.lat;
    const lng = safeFloat(editingData.longitude) ?? DEFAULT_CENTER.lng;
    const radiusVal = editingData.radius ? editingData.radius.toString() : "";

    setAddBranch({
      branchId:      editingData.branchId || editingData.id || null,
      userId:        user.user_id,
      branchName:    editingData.branch_name    || editingData.branchName    || "",
      branchAddress: editingData.branch_address || editingData.branchAddress || "",
      radius:        radiusVal,
      latitude:      lat,
      longitude:     lng,
      accuracy:      null,
    });

    /* setCenter always receives a valid object */
    setCenter({ lat, lng });
    setRadius(radiusVal);
    setInputValue(editingData.branch_address || editingData.branchAddress || "");
  }, [isEdit, editingData]);

  /* ── geolocation ── */
  const getCurrentLocation = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Geolocation not supported"));
          return;
        }
        const opts = { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 };
        navigator.geolocation.getCurrentPosition(resolve, (err) => {
          if (err.code === err.TIMEOUT || err.code === err.POSITION_UNAVAILABLE) {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 60000,
            });
          } else reject(err);
        }, opts);
      }),
    []
  );

  const getIniCurrentLocation = useCallback(async () => {
    try {
      setIsGettingLocation(true);
      setLocationError(null);

      const pos = await getCurrentLocation();
      const { latitude, longitude, accuracy } = pos.coords;

      /* Validate before using */
      const lat = safeFloat(latitude);
      const lng = safeFloat(longitude);
      if (lat === null || lng === null) throw new Error("Invalid coordinates from GPS");

      const newCenter = { lat, lng };
      setCenter(newCenter);
      setLocationAccuracy(accuracy);

      const address = await getDetailedAddress(lat, lng);
      const firstPart = address.split(",")[0].trim();
      const suggestedName = firstPart.length >= 3 && !/^\d+$/.test(firstPart) ? firstPart : "";

      setAddBranch((prev) => ({
        ...prev,
        latitude:      lat,
        longitude:     lng,
        branchAddress: address,
        accuracy,
        branchName:    prev.branchName || suggestedName,
      }));
      setInputValue(address);
      if (map) map.panTo(newCenter);
    } catch (err) {
      console.error("Location error:", err);
      const msg =
        err.code === 1 ? "Please enable location permissions and try again."
        : err.code === 2 ? "Location unavailable. Search for your address manually."
        : err.code === 3 ? "Location request timed out. Try again or search manually."
        : "Failed to get location. Please search manually or drag the marker.";
      setLocationError(msg);
      toast.error(msg);
    } finally {
      setIsGettingLocation(false);
    }
  }, [map, getCurrentLocation, getDetailedAddress, toast]);

  useEffect(() => {
    if (!isEdit && isLoaded) {
      setTimeout(getIniCurrentLocation, 500);
    }
  }, [isEdit, isLoaded]);

  /* ── circle drawing ──
     FIX: guard isValidLatLng(center) before creating Circle
     so new google.maps.Circle never receives NaN coords.
  ── */
  useEffect(() => {
    if (!window.google || !map) return;
    if (circleInstance) { circleInstance.setMap(null); }

    const r = parseInt(radius, 10);
    if (!isValidLatLng(center) || !Number.isFinite(r) || r <= 0) return;

    try {
      const c = new window.google.maps.Circle({
        center,
        radius: r,
        map,
        strokeColor: "#4285F4",
        strokeWeight: 2,
        fillOpacity: 0.2,
        fillColor: "#4285F4",
      });
      setCircleInstance(c);
    } catch (err) {
      console.error("Circle creation error:", err);
    }
  }, [radius, center, map]);

  /* ── fit bounds ── */
  useEffect(() => {
    if (!window.google || !map || !isValidLatLng(center)) return;
    const r = parseInt(radius, 10);
    if (!Number.isFinite(r) || r <= 0) return;

    try {
      const tmp = new window.google.maps.Circle({ center, radius: r });
      const bounds = tmp.getBounds();
      if (bounds) map.fitBounds(bounds);
    } catch (err) {
      console.error("fitBounds error:", err);
    }
  }, [radius, center, map]);

  /* ── handlers ── */
  const handleBranchChange = ({ target: { value } }) =>
    setAddBranch((p) => ({ ...p, branchName: value }));

  const handleRadiusChange = (e) => {
    const v = e.target.value;
    if (v === "" || /^\d+$/.test(v)) {
      setRadius(v);
      setAddBranch((p) => ({ ...p, radius: v }));
      const n = parseInt(v, 10);
      if (v && n < 100)  toast.warning("Minimum radius is 100 meters");
      if (v && n > 5000) toast.warning("Maximum radius is 5000 meters");
    }
  };

  const handleMarkerDrag = async (e) => {
    try {
      if (!e?.latLng) return;
      const lat = safeFloat(e.latLng.lat());
      const lng = safeFloat(e.latLng.lng());
      if (lat === null || lng === null) return;      /* ignore NaN drag events */

      const newCenter = { lat, lng };
      setCenter(newCenter);
      const address = await getDetailedAddress(lat, lng);
      setAddBranch((p) => ({ ...p, latitude: lat, longitude: lng, branchAddress: address }));
      setInputValue(address);
      setLocationAccuracy(null);
    } catch (err) {
      console.error("Marker drag error:", err);
      toast.error("Error updating location");
    }
  };

  const cancelActions = () => {
    setAddBranch({ branchId: null, userId: user.user_id, branchName: "", branchAddress: "", radius: "", latitude: null, longitude: null, accuracy: null });
    setRadius("");
    setInputValue("");
    setValidated(false);
    setLocationAccuracy(null);
  };

  /* ── places autocomplete ── */
  const debounce = (fn, delay) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
  };

  const fetchData = async (query) => {
    if (!query || query.length < 3) return;
    try {
      const res = await axios.get(
        `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${query}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
      );
      setOptions(res.data.predictions?.map((p) => ({ place: p.description, placeId: p.place_id })) || []);
    } catch { setOptions([]); }
  };

  const getPlaceDetails = async (placeId) => {
    try {
      const res = await axios.get(
        `https://maps.googleapis.com/maps/api/place/details/json?place_id=${placeId}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`
      );
      const loc = res.data.result?.geometry?.location;
      if (loc) return { lat: loc.lat, lng: loc.lng, address: res.data.result.formatted_address };
    } catch { }
    return null;
  };

  const debouncedFetch = debounce(fetchData, 500);

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInputValue(v);
    if (v.length >= 3) { debouncedFetch(v); setDropdownVisible(true); }
    else { setDropdownVisible(false); setOptions([]); }
  };

  const handleOptionSelect = async (option) => {
    if (!option?.placeId) return;
    const loc = await getPlaceDetails(option.placeId);

    if (loc) {
      const lat = safeFloat(loc.lat);
      const lng = safeFloat(loc.lng);
      if (lat !== null && lng !== null) {
        const newCenter = { lat, lng };
        setInputValue(loc.address);
        setCenter(newCenter);
        setAddBranch((p) => ({ ...p, branchAddress: loc.address, latitude: lat, longitude: lng }));
        if (map) map.panTo(newCenter);
        setLocationAccuracy(null);
      }
    } else {
      setInputValue(option.place);
      setAddBranch((p) => ({ ...p, branchAddress: option.place }));
    }
    setDropdownVisible(false);
    setOptions([]);
  };

  /* ── submit (add) ── */
  const handleSubmit = (e) => {
    const form = e.currentTarget;
    e.preventDefault();
    e.stopPropagation();
    if (!form.checkValidity()) { setValidated(true); return; }

    const lat = safeFloat(addBranch.latitude);
    const lng = safeFloat(addBranch.longitude);
    if (lat === null || lng === null) { toast.error("Please select a location on the map"); return; }

    if (!addBranch.radius) { toast.error("Please enter a radius"); return; }
    const r = parseInt(addBranch.radius, 10);
    if (!Number.isFinite(r) || r < 100 || r > 5000) {
      toast.error("Please enter a valid radius between 100 and 5000 meters");
      return;
    }

    setIsLoading(true);
    dispatch(saveBranch({ userId: user.user_id, branchName: addBranch.branchName, branchAddress: addBranch.branchAddress, radius: r, latitude: lat, longitude: lng }));
  };

  useEffect(() => {
    if (state.brnSuccess || state.brnError) {
      setIsLoading(false);
      if (state.brnSuccess) { toast.success(state.brnMessage || "Branch saved successfully"); cancelActions(); actionsDone(true, state.brnMessage); }
      else { toast.error(state.brnMessage || "Failed to save branch"); actionsDone(false, state.brnMessage); }
      dispatch(initState());
    }
  }, [state.brnSuccess, state.brnError]);

  /* ── submit (edit) ── */
  const handleEditSubmit = (e) => {
    e.preventDefault();
    if (!addBranch.branchName?.trim())    { toast.error("Please enter branch name");    return; }
    if (!addBranch.branchAddress?.trim()) { toast.error("Please enter branch address"); return; }
    if (!addBranch.radius)                { toast.error("Please enter a radius");       return; }

    const r   = parseInt(addBranch.radius, 10);
    const lat = safeFloat(addBranch.latitude);
    const lng = safeFloat(addBranch.longitude);

    if (!Number.isFinite(r) || r < 100 || r > 5000) { toast.error("Radius must be 100–5000 meters"); return; }
    if (lat === null || lng === null)                 { toast.error("Please select a location");       return; }
    if (!addBranch.branchId)                         { toast.error("Branch ID is missing");           return; }

    setIsLoading(true);
    dispatch(commonSaveData({
      path: updateBranchPath,
      data: { userId: user.user_id, branchId: addBranch.branchId, branchName: addBranch.branchName, branchAddress: addBranch.branchAddress, radius: r, latitude: lat, longitude: lng },
    }));
  };

  useEffect(() => {
    if (commonState.commonSuccess || commonState.commonError) {
      setIsLoading(false);
      if (commonState.commonSuccess) { toast.success(commonState.commonMessage || "Branch updated successfully"); cancelActions(); actionsDone(true, commonState.commonMessage); }
      else { toast.error(commonState.commonMessage || "Failed to update branch"); actionsDone(false, commonState.commonMessage); }
      dispatch(initCommonState());
    }
  }, [commonState.commonSuccess, commonState.commonError]);

  const preSubmitBsDtl = () => document.querySelector("#branchSubmitBtn")?.click();

  /* ── safe marker position — never passes NaN to Marker ── */
  const markerPos = isValidLatLng(center) ? center : null;

  if (loadError) return (
    <div className="p-4 text-center">
      <h3>Error loading Google Maps</h3>
      <p>Please check your API key and try again.</p>
      <Button onClick={handleCloseDialog} variant="contained">Close</Button>
    </div>
  );

  if (!isLoaded) return (
    <div className="p-4 text-center"><h3>Loading Map...</h3></div>
  );

  return (
    <>
      <DialogTitle className="dialog-title">
        <div className="dialog-title-icon">
          <span className="close-icon" onClick={handleCloseDialog}>✕</span>
          <span className="title">{isEdit ? "Update Branch" : "Add Branch"}</span>
        </div>
        <button
          className="universal-btn"
          style={{ width: "70px" }}
          onClick={preSubmitBsDtl}
          disabled={isLoading || isGettingLocation}
        >
          {isLoading ? "Saving..." : (isEdit ? "Update" : "Add")}
        </button>
      </DialogTitle>

      <DialogContent>
        <Form noValidate validated={validated} onSubmit={isEdit ? handleEditSubmit : handleSubmit}>
          <Row>
            <Col sm="6">
              <Form.Group className="mt-2">
                <Form.Label className="color-white">Branch Name *</Form.Label>
                <Form.Control
                  type="text" name="branchName"
                  value={addBranch.branchName || ""}
                  placeholder="Enter Branch Name"
                  onChange={handleBranchChange}
                  className="universal-input-one"
                  required disabled={isLoading}
                />
                <Form.Control.Feedback type="invalid">Enter branch name.</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col sm="6">
              <Form.Group className="mt-2">
                <Form.Label className="color-white">Radius (meters) *</Form.Label>
                <Form.Control
                  type="text" name="radius"
                  value={radius}
                  onChange={handleRadiusChange}
                  placeholder="Enter Radius (100-5000m)"
                  className="universal-input-one"
                  required disabled={isLoading}
                />
                <Form.Control.Feedback type="invalid">Enter radius value (100–5000 meters).</Form.Control.Feedback>
                <Form.Text className="text-muted">Enter a number between 100 and 5000 meters</Form.Text>
              </Form.Group>
            </Col>

            <Col sm="12">
              <Tooltip title="Get current location">
                <button
                  type="button" className="universal-btn"
                  onClick={getIniCurrentLocation}
                  style={{ position: "relative", top: "25px", float: "right", zIndex: 2, opacity: (isLoading || isGettingLocation) ? 0.7 : 1 }}
                  disabled={isLoading || isGettingLocation}
                >
                  <MdAddLocationAlt size={20} style={{ marginRight: "5px" }} />
                  {isGettingLocation ? "Getting Location..." : "Get Current Location"}
                </button>
              </Tooltip>
            </Col>

            <Col sm="12" style={{ position: "relative" }}>
              <Form.Group className="mt-2">
                <Form.Label className="color-white">Address *</Form.Label>
                <Form.Control
                  as="textarea" name="search"
                  placeholder="Search or enter address"
                  value={inputValue}
                  onChange={handleInputChange}
                  style={{ resize: "vertical", minHeight: "80px", maxHeight: "150px" }}
                  className="universal-input-one"
                  required disabled={isLoading} rows={3}
                />
                <Form.Control.Feedback type="invalid">Enter branch address.</Form.Control.Feedback>

                {dropdownVisible && options.length > 0 && (
                  <ul className="search-dropdown" style={{
                    listStyle: "none", padding: "8px 0", margin: 0,
                    position: "absolute", top: "100%", left: "12px", right: "12px",
                    maxHeight: "250px", border: "1px solid #444", borderRadius: "4px",
                    backgroundColor: theme === "dark" ? "#1a1a1a" : "#fff",
                    color: theme === "dark" ? "#fff" : "#333",
                    zIndex: 1000, overflow: "auto", boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                  }}>
                    {options.map((opt, i) => (
                      <li key={opt.placeId || i}
                        onClick={() => handleOptionSelect(opt)}
                        style={{ padding: "10px 16px", cursor: "pointer", borderBottom: i < options.length - 1 ? "1px solid #444" : "none" }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme === "dark" ? "#333" : "#f0f0f0"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                      >
                        {opt.place}
                      </li>
                    ))}
                  </ul>
                )}
              </Form.Group>
            </Col>

            {locationError && (
              <Col sm="12" className="mt-2">
                <div className="alert alert-warning">{locationError}</div>
              </Col>
            )}

            {locationAccuracy > 0 && (
              <Col sm="12" className="mt-2">
                <div className={`alert ${locationAccuracy > 100 ? "alert-info" : "alert-success"}`}>
                  <strong>📍 Location Accuracy:</strong> {Math.round(locationAccuracy)} meters
                  {locationAccuracy > 100 && <span> — Drag marker for better precision</span>}
                </div>
              </Col>
            )}

            <Col sm="12" className="mt-3 px-2" style={{ borderRadius: "8px", overflow: "hidden" }}>
              {/* GoogleMap always gets a valid center — never null, never NaN */}
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={safeCenter(center)}
                zoom={zoom}
                onLoad={(m) => setMap(m)}
                options={mapOptions}
              >
                {/* Marker only renders when position is valid */}
                {markerPos && (
                  <Marker
                    position={markerPos}
                    icon={{
                      url: MarkerIcon,
                      scaledSize: window.google
                        ? new window.google.maps.Size(32, 42)
                        : undefined,
                    }}
                    draggable={!isLoading}
                    onDragEnd={handleMarkerDrag}
                  />
                )}
              </GoogleMap>
            </Col>

            <Col sm="12" className="mt-2 text-muted">
              <small>📍 Drag the marker to adjust location precisely</small>
            </Col>
          </Row>

          <Button type="submit" id="branchSubmitBtn" style={{ display: "none" }}>
            Submit
          </Button>
        </Form>
      </DialogContent>

      <ToastContainer position="top-right" autoClose={2000} className="toast-custom" />
    </>
  );
}

export default React.memo(MapComponent);